import { motion } from "framer-motion";
import { BankLogo } from "@/components/landing/BankLogo";
import stepPos from "@/assets/landing/step-pos.png";
import stepInvoice from "@/assets/landing/step-invoice.png";
import stepPhone from "@/assets/landing/step-phone.png";
import toastLogo from "@/assets/toast-logo.png";
import squareLogo from "@/assets/square-logo.png";
import cloverLogo from "@/assets/clover-POS.svg";
import uberEatsLogo from "@/assets/uber-eats-logo.svg";
import doordashLogo from "@/assets/doordash-logo.png";
import grubhubLogo from "@/assets/grubhub-logo.jpg";

const bankLogos = [
  { name: "Chase", domain: "chase.com", color: "#117ACA" },
  { name: "Bank of America", domain: "bankofamerica.com", color: "#012169" },
  { name: "Wells Fargo", domain: "wellsfargo.com", color: "#D71E28" },
  { name: "Capital One", domain: "capitalone.com", color: "#004977" },
  { name: "Citi", domain: "citi.com", color: "#003B70" },
];

const posLogos = [
  { name: "Toast", logo: toastLogo },
  { name: "Square", logo: squareLogo },
  { name: "Clover", logo: cloverLogo },
  { name: "Uber Eats", logo: uberEatsLogo },
  { name: "DoorDash", logo: doordashLogo },
];

const steps = [
  {
    number: "1",
    title: "Connect your accounts",
    description:
      "Link your POS system (Square, Toast, Clover) and bank account. We pull real transaction data automatically.",
    image: stepPos,
    alt: "POS system in a restaurant kitchen connected to Dollar Bill",
  },
  {
    number: "2",
    title: "Upload invoices and train the system",
    description:
      "Forward vendor invoices by email or upload them directly. Dollar Bill learns how your business works — your costs, your categories, your vendors.",
    image: stepInvoice,
    alt: "Restaurant operators reviewing invoices on a laptop",
  },
  {
    number: "3",
    title: "Talk to Bill",
    description:
      "Ask any question. Bill gives you clear answers with the data to back them up, plus actions you can take right now.",
    image: stepPhone,
    alt: "Restaurant operator using Dollar Bill on phone",
  },
];

export const HowItWorksSection = () => {
  return (
    <section id="how-it-works" className="py-24 px-6 bg-stone">
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-midnight tracking-tight">
            Get started in{" "}
            <span className="text-vault-green">three steps.</span>
          </h2>
          <p className="text-lg text-slate-brand">
            You'll be up and running within a day.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Step 1 — Connect your accounts (with logos) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0, duration: 0.5 }}
            className="bg-white rounded-xl overflow-hidden border border-fog hover:shadow-lg transition-shadow duration-300"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={stepPos}
                alt="POS system in a restaurant kitchen connected to Dollar Bill"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-vault-green flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-sm">1</span>
              </div>
            </div>

            <div className="p-6">
              <h3 className="text-xl font-bold text-midnight mb-4">
                Connect your accounts
              </h3>

              <div className="space-y-3">
                <div>
                  <p className="text-[10px] font-semibold text-slate-brand uppercase tracking-wider mb-2">
                    Banks
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {bankLogos.map((bank, i) => (
                      <motion.div
                        key={bank.name}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.15 + i * 0.06 }}
                        className="w-11 h-11 rounded-lg bg-stone border border-fog p-1.5 flex items-center justify-center overflow-hidden"
                        title={bank.name}
                      >
                        <BankLogo name={bank.name} domain={bank.domain} color={bank.color} size={28} variant="symbol" />
                      </motion.div>
                    ))}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.15 + bankLogos.length * 0.06 }}
                      className="w-11 h-11 rounded-lg bg-vault-green/5 border border-vault-green/20 flex items-center justify-center"
                    >
                      <span className="text-[10px] font-semibold text-vault-green whitespace-nowrap">+more</span>
                    </motion.div>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-slate-brand uppercase tracking-wider mb-2">
                    POS & Delivery
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {posLogos.map((platform, i) => (
                      <motion.div
                        key={platform.name}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.06 }}
                        className="w-11 h-11 rounded-lg bg-stone border border-fog p-2 flex items-center justify-center"
                      >
                        <img
                          src={platform.logo}
                          alt={platform.name}
                          className="max-h-full w-auto object-contain"
                        />
                      </motion.div>
                    ))}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + posLogos.length * 0.06 }}
                      className="w-11 h-11 rounded-lg bg-vault-green/5 border border-vault-green/20 flex items-center justify-center"
                    >
                      <span className="text-[10px] font-semibold text-vault-green whitespace-nowrap">+more</span>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Steps 2 & 3 */}
          {steps.slice(1).map((step, i) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i + 1) * 0.12, duration: 0.5 }}
              className="bg-white rounded-xl overflow-hidden border border-fog hover:shadow-lg transition-shadow duration-300"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={step.image}
                  alt={step.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-vault-green flex items-center justify-center shadow-md">
                  <span className="text-white font-bold text-sm">
                    {step.number}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-midnight mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-brand text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
